// Seller Net Sheet / Buyer Closing Costs tab — renders the full closing-cost
// breakdown using calculateSellerNetSheet or calculateBuyerClosingCosts.

import React from 'react';
import CurrencyInput from '../../ui/CurrencyInput';
import useMediaQuery from '../../../hooks/useMediaQuery';
import {
    SELLER_NET_SHEET_DISCLAIMER,
    calculateProratedTaxes,
    calculateSellerNetSheetTotals,
    createHydratedSellerNetSheet
} from '../../../utils/sellerNetSheet';
import { formatCurrency, formatPercentDisplay } from '../../../utils/formatting';
import { usePortalLanguage } from '../../../contexts/PortalLanguageContext';

const sanitizePercentInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const cleaned = String(value).replace(/[%\s]/g, '').replace(/[^0-9.]/g, '');
    if (!cleaned) return '';
    const firstDecimalIndex = cleaned.indexOf('.');
    if (firstDecimalIndex === -1) return cleaned.slice(0, 4);
    const integerPart = cleaned.slice(0, firstDecimalIndex).slice(0, 2);
    const decimalPart = cleaned.slice(firstDecimalIndex + 1).replace(/\./g, '').slice(0, 2);
    return `${integerPart}.${decimalPart}`;
};

const sanitizeCurrencyInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const cleaned = String(value).replace(/[^0-9.]/g, '');
    if (!cleaned) return '';
    const [integerPart = '', ...decimalParts] = cleaned.split('.');
    const decimalPart = decimalParts.join('').slice(0, 2);
    return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
};

const formatCommasCurrency = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '0';
    const str = String(raw);
    const [intPart, decPart] = str.split('.');
    const formattedInt = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart ? `${formattedInt}.${decPart}` : formattedInt;
};

const toPercentDisplay = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = parseFloat((Number(val) * 100).toFixed(6));
    return Number.isFinite(num) ? sanitizePercentInput(String(num)) : '';
};

function PercentInput({ label, name, value, onChange }) {
    const [isFocused, setIsFocused] = React.useState(false);
    const [localDisplay, setLocalDisplay] = React.useState(() => toPercentDisplay(value));

    React.useEffect(() => {
        if (!isFocused) {
            setLocalDisplay(toPercentDisplay(value));
        }
    }, [value, isFocused]);

    const displayValue = isFocused ? localDisplay : (localDisplay ? `${localDisplay} %` : '');

    const handleInputChange = (event) => {
        const sanitized = sanitizePercentInput(event.target.value);
        setLocalDisplay(sanitized);
        const nextValue = sanitized === '' ? '' : String(Number(sanitized) / 100);
        onChange({ target: { name, value: nextValue } });
    };

    const handleFocus = () => setIsFocused(true);

    const handleBlur = () => {
        setIsFocused(false);
        setLocalDisplay(toPercentDisplay(value));
    };

    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">{label}</label>
            <input
                type="text"
                name={name}
                value={displayValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                inputMode="decimal"
                className="w-full rounded-md border border-[color:var(--border-color)] p-3"
            />
        </div>
    );
}

function FieldBlock({ title, subtitle, children, action = null }) {
    return (
        <section className="rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">{title}</h3>
                    {subtitle ? <p className="mt-1 text-sm text-[color:var(--text-muted)]">{subtitle}</p> : null}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function ScenarioOtherCostInput({ name, value, onChange }) {
    const [isFocused, setIsFocused] = React.useState(false);
    const [localDisplay, setLocalDisplay] = React.useState(() => formatCommasCurrency(value));

    React.useEffect(() => {
        if (!isFocused) {
            setLocalDisplay(formatCommasCurrency(value));
        }
    }, [value, isFocused]);

    const handleInputChange = React.useCallback((event) => {
        const sanitized = sanitizeCurrencyInput(event.target.value);
        setLocalDisplay(sanitized);
        onChange({ target: { name, value: sanitized } });
    }, [name, onChange]);

    const handleFocus = () => {
        setIsFocused(true);
        setLocalDisplay(sanitizeCurrencyInput(String(value ?? '')));
    };

    const handleBlur = () => {
        setIsFocused(false);
        if (!localDisplay.trim()) {
            setLocalDisplay('0');
            onChange({ target: { name, value: '0' } });
        } else {
            setLocalDisplay(formatCommasCurrency(localDisplay));
        }
    };

    return (
        <div className="relative flex items-center font-medium text-[color:var(--text-primary)]">
            <span className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2">$</span>
            <input
                type="text"
                name={name}
                value={localDisplay}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                inputMode="decimal"
                className="w-full border-0 bg-transparent p-0 pl-4 font-medium text-[color:var(--text-primary)] focus:outline-none focus:ring-0"
            />
        </div>
    );
}

function ScenarioCard({ index, scenario, scenarioInput, onChange, readOnly = false }) {
    const { t } = usePortalLanguage();
    return (
        <div
            className="rounded-3xl border shadow-sm overflow-hidden"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}
        >
            <div
                className="rounded-3xl p-5"
                style={{ background: 'linear-gradient(135deg, var(--surface-strong) 0%, var(--surface-muted) 100%)' }}
            >
                {readOnly ? (
                    <div className="text-lg font-semibold text-[color:var(--text-primary)]">{t(scenarioInput.name)}</div>
                ) : (
                    <input
                        type="text"
                        name={`scenarios.${index}.name`}
                        value={scenarioInput.name}
                        onChange={onChange}
                        className="w-full border-none bg-transparent p-0 text-lg font-semibold text-[color:var(--text-primary)] focus:outline-none focus:ring-0"
                    />
                )}
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[color:var(--text-muted)]">{t('Scenario {letter}', { letter: String.fromCharCode(65 + index) })}</p>
                <div className="mt-4">
                    {readOnly ? (
                        <div>
                            <div className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">{t('Sale Price')}</div>
                            <div className="rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] px-3 py-3 text-sm font-medium text-[color:var(--text-primary)]">
                                {scenarioInput.salePrice ? formatCurrency(scenarioInput.salePrice) : t('Not set')}
                            </div>
                        </div>
                    ) : (
                        <CurrencyInput
                            label="Sale Price"
                            name={`scenarios.${index}.salePrice`}
                            value={scenarioInput.salePrice}
                            onChange={onChange}
                        />
                    )}
                </div>
                <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="text-xs uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{t('Net Proceeds')}</div>
                    <div className="mt-2 text-3xl font-semibold text-[color:var(--financial-action-color)]">{formatCurrency(scenario.estimatedNetProceeds)}</div>
                </div>
            </div>
        </div>
    );
}

function SellerNetSheetTab({ formData, setFormData = null, readOnly = false, variant = 'full', maxScenarios = null }) {
    const isCompactLayout = useMediaQuery('(max-width: 1023px)');
    // Portal language (English default outside the client portal).
    const { t, formatShortDate } = usePortalLanguage();
    const formatDisplayDate = (value) => formatShortDate(value, t('Not set'));
    const isPortalVariant = variant === 'portal';
    const sellerNetSheet = React.useMemo(
        () => createHydratedSellerNetSheet(formData),
        [formData]
    );
    const scenarioTotals = React.useMemo(
        () => calculateSellerNetSheetTotals(sellerNetSheet),
        [sellerNetSheet]
    );
    const displayedScenarioTotals = React.useMemo(() => {
        const limit = Number.isFinite(maxScenarios) && maxScenarios > 0 ? maxScenarios : scenarioTotals.length;
        return scenarioTotals.slice(0, limit);
    }, [maxScenarios, scenarioTotals]);
    const displayedScenarioInputs = React.useMemo(() => {
        const limit = Number.isFinite(maxScenarios) && maxScenarios > 0 ? maxScenarios : sellerNetSheet.scenarios.length;
        return sellerNetSheet.scenarios.slice(0, limit);
    }, [maxScenarios, sellerNetSheet.scenarios]);
    const autoProratedTaxes = React.useMemo(
        () => calculateProratedTaxes({
            annualPropertyTaxes: sellerNetSheet.closingCosts?.annualPropertyTaxes,
            estimatedCloseDate: sellerNetSheet.estimatedCloseDate,
            prorationPeriod: sellerNetSheet.closingCosts?.prorationPeriod
        }),
        [sellerNetSheet]
    );
    const propertyAddress = [formData.propertyStreet, formData.propertyCityStateZip].filter(Boolean).join(', ') || formData.propertyAddress || t('Property address not set');
    const effectiveSellerNames = sellerNetSheet.sellerNames || t('Seller names not set');
    const lastUpdatedLabel = sellerNetSheet.updatedAt ? formatDisplayDate(sellerNetSheet.updatedAt) : t('Not saved yet');
    const comparisonSections = React.useMemo(() => ([
        {
            key: 'liens',
            title: 'Liens & Payoffs',
            rows: [
                {
                    key: 'firstMortgagePayoff',
                    label: 'First Mortgage Payoff',
                    context: '',
                    getValue: () => sellerNetSheet.liens.firstMortgagePayoff
                },
                {
                    key: 'secondMortgagePayoff',
                    label: 'Second Mortgage Payoff',
                    context: '',
                    getValue: () => sellerNetSheet.liens.secondMortgagePayoff
                },
                {
                    key: 'homeEquityLoan',
                    label: 'Home Equity Loan',
                    context: '',
                    getValue: () => sellerNetSheet.liens.homeEquityLoan
                },
                {
                    key: 'prepaymentPenalty',
                    label: 'Prepayment Penalty',
                    context: '',
                    getValue: () => sellerNetSheet.liens.prepaymentPenalty
                },
                {
                    key: 'additionalLien1',
                    label: 'Additional Lien 1',
                    context: '',
                    getValue: () => sellerNetSheet.liens.additionalLien1
                },
                {
                    key: 'additionalLien2',
                    label: 'Additional Lien 2',
                    context: '',
                    getValue: () => sellerNetSheet.liens.additionalLien2
                }
            ]
        },
        {
            key: 'brokerage',
            title: 'Brokerage Fees',
            rows: [
                {
                    key: 'adminFee',
                    label: 'Admin Fee',
                    context: 'Flat fee',
                    getValue: () => sellerNetSheet.brokerage.adminFee
                },
                {
                    key: 'sellerCommission',
                    label: 'Seller Commission',
                    context: t('{rate} seller commission', { rate: formatPercentDisplay(sellerNetSheet.brokerage.sellerCommissionRate || 0) }),
                    getValue: (scenario) => scenario.sellerCommission
                },
                {
                    key: 'buyerCommission',
                    label: 'Buyer Commission',
                    context: t('{rate} buyer commission', { rate: formatPercentDisplay(sellerNetSheet.brokerage.buyerCommissionRate || 0) }),
                    getValue: (scenario) => scenario.buyerCommission
                }
            ]
        },
        {
            key: 'closing',
            title: 'Closing Costs',
            rows: [
                {
                    key: 'proratedTaxes',
                    label: 'Prorated Taxes',
                    context: sellerNetSheet.closingCosts.proratedTaxesMode === 'manual'
                        ? t('Manual override')
                        : t('Auto • {period}', { period: t(sellerNetSheet.closingCosts.prorationPeriod || 'long') }),
                    getValue: (scenario) => scenario.proratedTaxes
                },
                {
                    key: 'sellerClosingFee',
                    label: 'Seller Closing Fee',
                    context: 'To title company',
                    getValue: () => sellerNetSheet.closingCosts.sellerClosingFee
                },
                {
                    key: 'deedPreparationFee',
                    label: 'Deed Preparation',
                    context: '',
                    getValue: () => sellerNetSheet.closingCosts.deedPreparationFee
                },
                {
                    key: 'titleExtraFees',
                    label: 'Title Extra Fees',
                    context: '',
                    getValue: () => sellerNetSheet.closingCosts.titleExtraFees
                },
                {
                    key: 'ownerTitleInsurance',
                    label: 'Owner Title Insurance',
                    context: '',
                    getValue: () => sellerNetSheet.closingCosts.ownerTitleInsurance
                },
                {
                    key: 'transferTax',
                    label: 'Transfer Tax',
                    context: t('{rate} per $1,000 + {fee} land fee', {
                        rate: formatCurrency(sellerNetSheet.closingCosts.transferTaxPerThousand).replace('.00', ''),
                        fee: formatCurrency(sellerNetSheet.closingCosts.landTransferFee)
                    }),
                    getValue: (scenario) => scenario.transferTax
                }
            ]
        },
        {
            key: 'other',
            title: 'Other Costs',
            rows: [
                {
                    key: 'hoaFees',
                    label: 'HOA Fees',
                    context: 'Editable per scenario',
                    editable: true,
                    getValue: (scenario) => scenario.otherFees?.hoaFees ?? sellerNetSheet.otherFees.hoaFees
                },
                {
                    key: 'repairsFromInspection',
                    label: 'Repairs From Inspection',
                    context: 'Editable per scenario',
                    editable: true,
                    getValue: (scenario) => scenario.otherFees?.repairsFromInspection ?? sellerNetSheet.otherFees.repairsFromInspection
                },
                {
                    key: 'homeWarranty',
                    label: 'Home Warranty',
                    context: 'Editable per scenario',
                    editable: true,
                    getValue: (scenario) => scenario.otherFees?.homeWarranty ?? sellerNetSheet.otherFees.homeWarranty
                },
                {
                    key: 'closingCostHelp',
                    label: 'Closing Cost Help',
                    context: 'Editable per scenario',
                    editable: true,
                    getValue: (scenario) => scenario.otherFees?.closingCostHelp ?? sellerNetSheet.otherFees.closingCostHelp
                }
            ]
        },
        {
            key: 'totals',
            title: 'Totals',
            rows: [
                {
                    key: 'totalEstimatedCosts',
                    label: 'Total Estimated Costs',
                    context: 'All payoffs and fees',
                    emphasize: 'cost',
                    getValue: (scenario) => scenario.totalEstimatedCosts
                }
            ]
        }
    ].map(section => ({
        ...section,
        title: t(section.title),
        rows: section.rows.map(row => ({ ...row, label: t(row.label), context: t(row.context) }))
    }))), [sellerNetSheet, t]);

    const patchSellerNetSheet = React.useCallback((updater) => {
        if (readOnly || typeof setFormData !== 'function') return;
        setFormData((prev) => {
            const current = createHydratedSellerNetSheet(prev);
            const next = typeof updater === 'function' ? updater(current) : current;
            return { ...prev, sellerNetSheet: next };
        });
    }, [readOnly, setFormData]);

    const handleFieldChange = React.useCallback((event) => {
        if (readOnly) return;
        const { name, value, type, checked } = event.target;
        const nextValue = type === 'checkbox' ? checked : value;
        const path = name.split('.');
        patchSellerNetSheet((current) => {
            const next = {
                ...current,
                liens: { ...current.liens },
                brokerage: { ...current.brokerage },
                closingCosts: { ...current.closingCosts },
                otherFees: { ...current.otherFees },
                scenarios: current.scenarios.map((scenario) => ({
                    ...scenario,
                    otherFees: { ...(scenario.otherFees || {}) }
                }))
            };

            if (path[0] === 'scenarios') {
                const scenarioIndex = Number(path[1]);
                if (path[2] === 'otherFees' && path.length > 3) {
                    next.scenarios[scenarioIndex] = {
                        ...next.scenarios[scenarioIndex],
                        otherFees: {
                            ...(next.scenarios[scenarioIndex].otherFees || {}),
                            [path[3]]: nextValue
                        }
                    };
                    return next;
                }

                const scenarioKey = path[2];
                next.scenarios[scenarioIndex] = { ...next.scenarios[scenarioIndex], [scenarioKey]: nextValue };
                return next;
            }

            if (path.length === 1) {
                next[path[0]] = nextValue;
                return next;
            }

            next[path[0]][path[1]] = nextValue;
            return next;
        });
    }, [patchSellerNetSheet, readOnly]);

    const handleResetDefaults = () => {
        setFormData((prev) => ({ ...prev, sellerNetSheet: createHydratedSellerNetSheet({ ...prev, sellerNetSheet: {} }) }));
    };

    const handleDuplicateListPrice = () => {
        const listPrice = formData.listPrice || formData.salePrice || '';
        if (!listPrice) return;
        patchSellerNetSheet((current) => {
            const next = { ...current, scenarios: current.scenarios.map((scenario) => ({ ...scenario })) };
            next.scenarios[0].salePrice = listPrice;
            return next;
        });
    };

    // Mobile / compact comparison board. The desktop table needs 980px and scrolls
    // horizontally; here we drop the rows that are identical across scenarios into a
    // collapsed "Same across all" block and compare only the ~10 rows that actually
    // vary, so 2-5 scenarios sit side by side without horizontal scroll. A row varies
    // when its getValue takes a scenario argument (arity ≥ 1); zero-arity rows are shared.
    const renderScenarioComparisonCards = () => {
        const scenarios = displayedScenarioTotals;
        const n = scenarios.length;
        const letter = (i) => String.fromCharCode(65 + i);
        const colGrid = { display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: '8px' };
        const sharedRows = comparisonSections.flatMap(s => s.rows.filter(r => r.getValue.length === 0));

        const ValueRow = ({ label, context, emphasize, cells }) => (
            <div className="py-2.5 border-t border-[color:var(--border-color)] first:border-t-0">
                <div className="text-[12px] font-medium text-[color:var(--text-primary)]">{label}</div>
                {context ? <div className="text-[10.5px] text-[color:var(--text-muted)] mt-0.5">{context}</div> : null}
                <div style={colGrid} className="mt-1.5">
                    {cells.map((cell, i) => (
                        <div key={i} className="min-w-0 text-center">
                            {cell.input ? cell.input : (
                                <span
                                    className={
                                        emphasize === 'net'
                                            ? 'text-[13px] font-semibold tabular-nums text-[color:var(--financial-action-color)]'
                                            : emphasize === 'cost'
                                                ? 'text-[13px] font-semibold tabular-nums text-[color:var(--text-primary)]'
                                                : 'text-[13px] font-medium tabular-nums text-[color:var(--text-primary)]'
                                    }
                                    style={{ overflowWrap: 'anywhere' }}
                                >
                                    {cell.text}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );

        return (
            <div className="rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] p-4 shadow-sm">
                {/* Scenario column headers */}
                <div style={colGrid}>
                    {scenarios.map((scenario, i) => (
                        <div key={scenario.id} className="min-w-0 text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                                {t('Scenario {letter}', { letter: letter(i) })}
                            </div>
                            <div className="text-[11px] font-medium text-[color:var(--text-primary)] truncate" title={t(displayedScenarioInputs[i].name)}>
                                {t(displayedScenarioInputs[i].name)}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Net proceeds — the headline comparison */}
                <div className="mt-3 rounded-xl bg-[color:var(--surface-muted)] px-3 py-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)] text-center">
                        {t('Net Proceeds')}
                    </div>
                    <div style={colGrid} className="mt-1.5">
                        {scenarios.map(scenario => (
                            <div key={scenario.id} className="min-w-0 text-center text-[15px] font-semibold tabular-nums text-[color:var(--financial-action-color)]" style={{ overflowWrap: 'anywhere' }}>
                                {formatCurrency(scenario.estimatedNetProceeds)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sale price (editable per scenario) */}
                <div className="mt-3">
                    <ValueRow
                        label={t('Sale Price')}
                        cells={scenarios.map((scenario, i) => ({
                            input: !readOnly ? (
                                <div className="rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-info)] px-2 py-1 text-[12px] text-left">
                                    <ScenarioOtherCostInput
                                        name={`scenarios.${i}.salePrice`}
                                        value={displayedScenarioInputs[i].salePrice}
                                        onChange={handleFieldChange}
                                    />
                                </div>
                            ) : null,
                            text: formatCurrency(displayedScenarioInputs[i].salePrice),
                        }))}
                    />

                    {/* Only the rows that vary across scenarios, grouped by section */}
                    {comparisonSections.map(section => {
                        const varying = section.rows.filter(r => r.getValue.length >= 1);
                        if (varying.length === 0) return null;
                        return (
                            <div key={section.key} className="mt-2">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)] mt-2 mb-0.5">
                                    {section.title}
                                </div>
                                {varying.map(row => (
                                    <ValueRow
                                        key={row.key}
                                        label={row.label}
                                        context={row.context}
                                        emphasize={row.emphasize}
                                        cells={scenarios.map((scenario, i) => ({
                                            input: row.editable && !readOnly ? (
                                                <div className="rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-info)] px-2 py-1 text-[12px] text-left">
                                                    <ScenarioOtherCostInput
                                                        name={`scenarios.${i}.otherFees.${row.key}`}
                                                        value={displayedScenarioInputs[i].otherFees?.[row.key] ?? row.getValue(scenario)}
                                                        onChange={handleFieldChange}
                                                    />
                                                </div>
                                            ) : null,
                                            text: formatCurrency(row.getValue(scenario)),
                                        }))}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </div>

                {/* Shared rows — identical across every scenario, collapsed by default */}
                {sharedRows.length > 0 && (
                    <details className="mt-3 rounded-xl border border-[color:var(--border-color)] bg-[color:var(--surface-muted)]">
                        <summary className="cursor-pointer list-none px-4 py-2.5 text-[12px] font-semibold text-[color:var(--text-primary)]">
                            {t('Same across all scenarios')}
                        </summary>
                        <div className="px-4 pb-3 divide-y divide-[color:var(--border-color)]">
                            {sharedRows.map(row => (
                                <div key={row.key} className="flex items-start justify-between gap-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-[12px] font-medium text-[color:var(--text-primary)]">{row.label}</div>
                                        {row.context ? <div className="text-[10.5px] text-[color:var(--text-muted)] mt-0.5">{row.context}</div> : null}
                                    </div>
                                    <div className="text-[13px] font-medium tabular-nums text-[color:var(--text-primary)] text-right whitespace-nowrap">
                                        {formatCurrency(row.getValue())}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {!isPortalVariant && (
                <>
                    <FieldBlock title="Inputs & Assumptions" subtitle="Review the transaction-derived defaults, then override any field that needs a seller-specific estimate.">
                        <div className="space-y-4">
                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">Prepared Date</label>
                                    <input type="date" name="preparedDate" value={sellerNetSheet.preparedDate} onChange={handleFieldChange} className="w-full rounded-md border border-[color:var(--border-color)] p-3" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">Estimated Close Date</label>
                                    <input type="date" name="estimatedCloseDate" value={sellerNetSheet.estimatedCloseDate} onChange={handleFieldChange} className="w-full rounded-md border border-[color:var(--border-color)] p-3" />
                                </div>
                                <CurrencyInput label="Admin Fee" name="brokerage.adminFee" value={sellerNetSheet.brokerage.adminFee} onChange={handleFieldChange} />
                                <PercentInput label="Seller Commission Rate" name="brokerage.sellerCommissionRate" value={sellerNetSheet.brokerage.sellerCommissionRate} onChange={handleFieldChange} />
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">Proration Type</label>
                                    <select
                                        name="closingCosts.prorationPeriod"
                                        value={sellerNetSheet.closingCosts.prorationPeriod || 'long'}
                                        onChange={handleFieldChange}
                                        className="w-full rounded-md border border-[color:var(--border-color)] p-3"
                                    >
                                        <option value="short">Short</option>
                                        <option value="long">Long</option>
                                    </select>
                                </div>
                                <CurrencyInput label="Annual Property Taxes" name="closingCosts.annualPropertyTaxes" value={sellerNetSheet.closingCosts.annualPropertyTaxes} onChange={handleFieldChange} />
                                <div aria-hidden="true" className="hidden xl:block" />
                                <PercentInput label="Buyer Commission Rate" name="brokerage.buyerCommissionRate" value={sellerNetSheet.brokerage.buyerCommissionRate} onChange={handleFieldChange} />
                            </div>
                        </div>
                    </FieldBlock>

                    <FieldBlock title="Liens & Payoffs" subtitle="These obligations apply to every scenario.">
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <CurrencyInput label="First Mortgage Payoff" name="liens.firstMortgagePayoff" value={sellerNetSheet.liens.firstMortgagePayoff} onChange={handleFieldChange} />
                            <CurrencyInput label="Second Mortgage Payoff" name="liens.secondMortgagePayoff" value={sellerNetSheet.liens.secondMortgagePayoff} onChange={handleFieldChange} />
                            <CurrencyInput label="Home Equity Loan" name="liens.homeEquityLoan" value={sellerNetSheet.liens.homeEquityLoan} onChange={handleFieldChange} />
                            <CurrencyInput label="Prepayment Penalty" name="liens.prepaymentPenalty" value={sellerNetSheet.liens.prepaymentPenalty} onChange={handleFieldChange} />
                            <CurrencyInput label="Additional Lien 1" name="liens.additionalLien1" value={sellerNetSheet.liens.additionalLien1} onChange={handleFieldChange} />
                            <CurrencyInput label="Additional Lien 2" name="liens.additionalLien2" value={sellerNetSheet.liens.additionalLien2} onChange={handleFieldChange} />
                        </div>
                    </FieldBlock>

                    <FieldBlock title="Closing Costs" subtitle="Switch prorations to manual whenever a title quote or tax figure needs to override the estimate.">
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">Prorated Taxes Mode</label>
                                <select name="closingCosts.proratedTaxesMode" value={sellerNetSheet.closingCosts.proratedTaxesMode} onChange={handleFieldChange} className="w-full rounded-md border border-[color:var(--border-color)] p-3">
                                    <option value="auto">Auto-calculate</option>
                                    <option value="manual">Manual override</option>
                                </select>
                            </div>
                            {sellerNetSheet.closingCosts.proratedTaxesMode === 'manual' ? (
                                <CurrencyInput
                                    label="Prorated Taxes Amount"
                                    name="closingCosts.proratedTaxesAmount"
                                    value={sellerNetSheet.closingCosts.proratedTaxesAmount}
                                    onChange={handleFieldChange}
                                />
                            ) : (
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[color:var(--text-secondary)]">Prorated Taxes Amount</label>
                                    <input
                                        type="text"
                                        value={formatCurrency(autoProratedTaxes)}
                                        readOnly
                                        className="w-full rounded-md border border-[color:var(--border-color)] p-3"
                                    />
                                </div>
                            )}
                            <CurrencyInput label="Seller Closing Fee" name="closingCosts.sellerClosingFee" value={sellerNetSheet.closingCosts.sellerClosingFee} onChange={handleFieldChange} />
                            <CurrencyInput label="Deed Preparation Fee" name="closingCosts.deedPreparationFee" value={sellerNetSheet.closingCosts.deedPreparationFee} onChange={handleFieldChange} />
                            <CurrencyInput label="Title Extra Fees" name="closingCosts.titleExtraFees" value={sellerNetSheet.closingCosts.titleExtraFees} onChange={handleFieldChange} />
                            <CurrencyInput label="Owner Title Insurance" name="closingCosts.ownerTitleInsurance" value={sellerNetSheet.closingCosts.ownerTitleInsurance} onChange={handleFieldChange} />
                            <CurrencyInput label="Transfer Tax Per $1,000" name="closingCosts.transferTaxPerThousand" value={sellerNetSheet.closingCosts.transferTaxPerThousand} onChange={handleFieldChange} />
                            <CurrencyInput label="Land Transfer Fee" name="closingCosts.landTransferFee" value={sellerNetSheet.closingCosts.landTransferFee} onChange={handleFieldChange} />
                        </div>
                    </FieldBlock>
                </>
            )}

            <section
                className="rounded-3xl border p-6 shadow-sm"
                style={{
                    borderColor: 'var(--border-color)',
                    background: 'linear-gradient(135deg, var(--surface-primary) 0%, var(--surface-muted) 100%)'
                }}
            >
                <div className="w-full">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--action-link-color)]">{t('Seller Proceeds')}</p>
                    <p className="mt-2 text-sm text-[color:var(--text-muted)]">{t('Estimated seller proceeds based on current transaction data and assumptions.')}</p>
                    <div className="mt-5 grid gap-3 text-sm text-[color:var(--text-secondary)] md:grid-cols-3">
                        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}>
                            <div className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{t('Property')}</div>
                            <div className="mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>{propertyAddress}</div>
                        </div>
                        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}>
                            <div className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{t('Seller(s)')}</div>
                            <div className="mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>{effectiveSellerNames}</div>
                        </div>
                        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}>
                            <div className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{t('Prepared Date')}</div>
                            <div className="mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>{formatDisplayDate(sellerNetSheet.preparedDate)}</div>
                        </div>
                        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}>
                            <label className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
                                {t('Expected Closing Date')}
                            </label>
                            {readOnly ? (
                                <div className="mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>{formatDisplayDate(sellerNetSheet.estimatedCloseDate)}</div>
                            ) : (
                                <input
                                    type="date"
                                    name="estimatedCloseDate"
                                    value={sellerNetSheet.estimatedCloseDate}
                                    onChange={handleFieldChange}
                                    className="mt-2 w-full rounded-md border border-[color:var(--border-color)] p-3 text-sm font-medium text-[color:var(--text-primary)]"
                                />
                            )}
                        </div>
                        <div aria-hidden="true" className="hidden md:block" />
                        <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}>
                            <div className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{t('Last Updated')}</div>
                            <div className="mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>{lastUpdatedLabel}</div>
                        </div>
                    </div>
                </div>
            </section>

            <FieldBlock
                title={t('Scenario Comparison')}
                subtitle={t('Review pricing scenarios and seller proceeds in one comparison board.')}
                action={!readOnly && !isPortalVariant ? (
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={handleDuplicateListPrice} className="rounded-md border border-[color:var(--border-color)] px-3 py-2 text-sm font-medium text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-hover)]">Use list price for Scenario A</button>
                        <button type="button" onClick={handleResetDefaults} className="rounded-md border border-[color:var(--border-color)] px-3 py-2 text-sm font-medium text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-hover)]">Reset to defaults</button>
                    </div>
                ) : null}
            >
                {isCompactLayout ? (
                    renderScenarioComparisonCards()
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-[980px] w-full border-separate border-spacing-0">
                            <thead>
                                <tr>
                                    <th className="align-top pr-4 pb-3 text-left" />
                                    {displayedScenarioTotals.map((scenario, index) => (
                                        <th key={scenario.id} className="align-top px-2 pb-3 text-left">
                                            <ScenarioCard
                                                index={index}
                                                scenario={scenario}
                                                scenarioInput={displayedScenarioInputs[index]}
                                                onChange={handleFieldChange}
                                                readOnly={readOnly}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonSections.map((section, sectionIndex) => (
                                    <React.Fragment key={section.key}>
                                        <tr>
                                            <td
                                                className="pr-4 pt-0 pb-0 align-top"
                                            >
                                                <div
                                                    className={`px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] ${sectionIndex === 0 ? 'rounded-t-2xl overflow-hidden' : ''}`}
                                                    style={{
                                                        color: 'var(--text-muted)',
                                                        backgroundColor: 'var(--surface-muted)',
                                                        borderLeft: '1px solid var(--border-color)',
                                                        borderRight: '1px solid var(--border-color)',
                                                        borderTop: '1px solid var(--border-color)'
                                                    }}
                                                >
                                                    {section.title}
                                                </div>
                                            </td>
                                            {displayedScenarioTotals.map((scenario, scenarioIndex) => (
                                                <td key={`${section.key}-${scenario.id}-section`} className={`relative px-2 pt-0 pb-0 align-top ${scenarioIndex === 0 ? 'pl-2' : ''}`}>
                                                    <div
                                                        className={`pointer-events-none absolute inset-y-0 left-2 right-2 ${sectionIndex === 0 ? 'rounded-t-2xl overflow-hidden' : ''}`}
                                                        style={{
                                                            backgroundColor: 'var(--surface-primary)',
                                                            borderLeft: '1px solid var(--border-color)',
                                                            borderRight: '1px solid var(--border-color)',
                                                            borderTop: '1px solid var(--border-color)'
                                                        }}
                                                    />
                                                    <div className="relative min-h-[28px]" />
                                                </td>
                                            ))}
                                        </tr>
                                        {section.rows.map((row, rowIndex) => {
                                            const isLastTableRow = sectionIndex === comparisonSections.length - 1 && rowIndex === section.rows.length - 1;
                                            return (
                                            <tr key={row.key}>
                                                <td
                                                    className="pr-4 pt-0 pb-0 align-top"
                                                >
                                                    <div
                                                        className={`px-4 py-3 ${isLastTableRow ? 'rounded-b-2xl' : ''}`}
                                                        style={{
                                                            backgroundColor: row.editable ? 'var(--surface-info)' : 'var(--surface-muted)',
                                                            borderLeft: '1px solid var(--border-color)',
                                                            borderRight: '1px solid var(--border-color)',
                                                            borderTop: '1px solid var(--border-color)',
                                                            borderBottom: isLastTableRow ? '1px solid var(--border-color)' : 'none'
                                                        }}
                                                    >
                                                        <div className={row.emphasize ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium text-[color:var(--text-primary)]'}>
                                                            {row.label}
                                                        </div>
                                                        {row.context ? (
                                                            <div className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                                                                {row.context}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </td>
                                                {displayedScenarioTotals.map((scenario, scenarioIndex) => (
                                                    <td
                                                        key={`${row.key}-${scenario.id}`}
                                                        className="relative px-2 pt-0 pb-0 align-top"
                                                    >
                                                        <div
                                                            className={`pointer-events-none absolute inset-y-0 left-2 right-2 ${isLastTableRow ? 'rounded-b-2xl' : ''}`}
                                                            style={{
                                                                backgroundColor: 'var(--surface-primary)',
                                                                borderLeft: '1px solid var(--border-color)',
                                                                borderRight: '1px solid var(--border-color)',
                                                                borderTop: '1px solid var(--border-color)',
                                                                borderBottom: isLastTableRow ? '1px solid var(--border-color)' : 'none'
                                                            }}
                                                        />
                                                        <div
                                                            className="relative z-10 px-4 py-3"
                                                        >
                                                            {row.editable && !readOnly ? (
                                                                <ScenarioOtherCostInput
                                                                    name={`scenarios.${scenarioIndex}.otherFees.${row.key}`}
                                                                    value={displayedScenarioInputs[scenarioIndex].otherFees?.[row.key] ?? row.getValue(scenario)}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            ) : (
                                                                <div
                                                                    className={
                                                                        row.emphasize === 'net'
                                                                            ? 'text-lg font-semibold text-[color:var(--financial-action-color)]'
                                                                            : row.emphasize === 'cost'
                                                                                ? 'font-semibold text-[color:var(--text-primary)]'
                                                                                : 'font-medium text-[color:var(--text-primary)]'
                                                                    }
                                                                >
                                                                    {formatCurrency(row.getValue(scenario))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="pr-4 pt-4" />
                                    {displayedScenarioTotals.map((scenario, index) => (
                                        <td key={`${scenario.id}-footer-card`} className="px-2 pt-4 align-top">
                                            <ScenarioCard
                                                index={index}
                                                scenario={scenario}
                                                scenarioInput={displayedScenarioInputs[index]}
                                                onChange={handleFieldChange}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </FieldBlock>

            <FieldBlock title={t('Notes & Assumptions')} subtitle={t('Capture seller-specific assumptions and keep the estimate language attached to the transaction.')}>
                <div className="space-y-4">
                    <div>
                        {readOnly ? (
                            <div className="min-h-[120px] rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-muted)] p-4 text-sm text-[color:var(--text-secondary)] whitespace-pre-wrap">
                                {sellerNetSheet.notes || t('No notes or assumptions have been shared yet.')}
                            </div>
                        ) : (
                            <textarea
                                name="notes"
                                value={sellerNetSheet.notes}
                                onChange={handleFieldChange}
                                rows={5}
                                className="w-full rounded-md border border-[color:var(--border-color)] p-3"
                                placeholder="Add payoff assumptions, timing notes, seller goals, or title-company caveats."
                            />
                        )}
                    </div>
                    <div
                        className="rounded-2xl border p-4 text-sm"
                        style={{
                            borderColor: 'var(--border-color)',
                            backgroundColor: 'var(--surface-muted)',
                            color: 'var(--text-primary)'
                        }}
                    >
                        <p>{t(SELLER_NET_SHEET_DISCLAIMER)}</p>
                    </div>
                </div>
            </FieldBlock>
        </div>
    );
}

export default SellerNetSheetTab;
